import { useCallback, useEffect, useState } from 'react';

import { useDeps } from '@Contexts/DI';
import { useRequest } from '@Contexts/Request';

interface JivoSiteAPI extends Window {
  jivo_api: {
    open(): void;
  };
  jivo_onLoadCallback(): void;
  jivo_cstm_widget: HTMLDivElement;
}

interface UseCustomJivositeParams {
  onJivoOpenFailed(): void;
}

declare const window: JivoSiteAPI;

function useCustomJivosite(params: UseCustomJivositeParams) {
  const { onJivoOpenFailed } = params;
  const [isLoaded, setIsLoaded] = useState(false);
  const { analytics } = useDeps();
  const { isBlr } = useRequest();

  const jivoOnLoadCallback = useCallback(() => {
    if (!isBlr) return;

    const alreadyLoaded = document.getElementById('jivo_custom_widget');

    if (alreadyLoaded) {
      setIsLoaded(true);
      return;
    }

    window.jivo_onLoadCallback = () => {
      // добавляем на страницу пустой элемент с кастомной иконкой чата, чтобы скрыть стандартный виджет Jivosite
      const jivoCustomWidget = document.createElement('div');
      window.jivo_cstm_widget = jivoCustomWidget;
      jivoCustomWidget.setAttribute('id', 'jivo_custom_widget');

      document.body.appendChild(jivoCustomWidget);

      // как только Jivosite загружается на странице, вызывается коллбек jivo_onLoadCallback, в котором показываем кнопку чата
      setIsLoaded(true);
    };
  }, [isBlr]);

  const openWidget = useCallback(() => {
    if (isLoaded && window.jivo_api) {
      window.jivo_api.open();

      analytics.dispatchEvent('jivo.open');
    } else {
      // выполняем другую логику, если виджет Jivosite не удалось открыть
      onJivoOpenFailed();
    }
  }, [isLoaded, analytics, onJivoOpenFailed]);

  useEffect(() => {
    if (!isLoaded) {
      jivoOnLoadCallback();
    }
  }, [isLoaded, jivoOnLoadCallback]);

  return {
    isLoaded,
    openWidget,
  };
}

export default useCustomJivosite;
